/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ProfilePitch from 'components/ProfilePitch';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import ProfileSummary from 'modules/misc/components/profile-summary/ProfileSummary';
import ProfileApprovalStatus from 'modules/brands/components/profile-approval-status/ProfileApprovalStatus';

import { fetchBrandProfile, resetBrandProfile } from '../reducers/brandProfile';

import { isAdmin } from 'utils/account';
import { canUserEditBrandProfile } from 'utils/accessRules';
import { t } from 'utils/language';
import BrandProfileAdminPanel from 'modules/admin/components/brand-profile-admin-panel/BrandProfileAdminPanel';

import type {
  Account,
  BrandProfile as BrandProfileType,
  ImageType,
  Organization,
  Role
} from 'types';


type OwnProps = {
  agencyId?: string,
  profileId: number,
  coverImageType?: ImageType,
  isSpecialtyAdminPage?: boolean
};

type StateProps = {
  brandProfile: BrandProfileType,
  currentOrganization: Organization,
  currentAccount: Account,
  roles: Array<Role>
};

type DispatchProps = {
  dispatchFetchBrandProfile: (a: number) => void,
  dispatchResetBrandProfile: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

class BrandProfile extends React.PureComponent<Props> {
  componentDidMount() {
    this.fetchBrandProfile(this.props);
  }

  componentWillUnmount() {
    const { dispatchResetBrandProfile } = this.props;

    dispatchResetBrandProfile();
  }

  fetchBrandProfile(nextProps: Props) {
    const { profileId, dispatchFetchBrandProfile } = nextProps;

    dispatchFetchBrandProfile(profileId);
  }

  handleApprovalStatusChanged = () => {
    this.fetchBrandProfile(this.props);
  };

  render() {
    const {
      currentAccount,
      currentOrganization,
      coverImageType,
      brandProfile,
      agencyId,
      isSpecialtyAdminPage,
      roles
    } = this.props;

    if (!brandProfile.id) {
      return null;
    }

    const canEdit = canUserEditBrandProfile(brandProfile, currentOrganization, currentAccount, roles);

    let editLink = agencyId
      ? `/agency/${agencyId}/settings/profile/edit`
      : `/organizations/${currentOrganization.id}/brand/settings/edit`;

    if (isSpecialtyAdminPage) {
      editLink = agencyId
        ? `/admin/agency/${agencyId}/settings/profile/edit`
        : `/admin/brand/profiles/${brandProfile.id}/settings/edit`;
    }

    return (
      <section className="brand-profile layout-container-narrow">
        {
          isAdmin(currentAccount, roles) && (
            <React.Fragment>
              <BrandProfileAdminPanel
                brandProfile={brandProfile}
                handleApprovalStatusChanged={this.handleApprovalStatusChanged}
              />
              <VerticalSpacing type="large" />
            </React.Fragment>
          )
        }
        {
          canEdit &&
            <ProfileApprovalStatus
              profile={brandProfile}
              profileType="brand"
            />
        }

        <ProfileSummary
          canEdit={false}
          profile={brandProfile}
          coverImageType={coverImageType}
        >
          <div className="brand-profile__pitch">
            <ProfilePitch
              pitch={brandProfile.pitch}
            />
          </div>
        </ProfileSummary>

        {
          canEdit &&
            <footer className="call-to-action">
              <ActionButton
                link={editLink}
                data={{ qa: 'brand-profile__button--edit' }}
              >
                {t('edit_profile_action_button')}
              </ActionButton>
            </footer>
        }
      </section>
    );
  }
}

const mapStateToProps = ({ brandProfile, organizations, account }): StateProps => ({
  brandProfile: brandProfile.profile,
  currentOrganization: organizations.currentOrganization,
  currentAccount: account.currentAccount,
  roles: account.roles
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchBrandProfile: (profileId: number) => dispatch(fetchBrandProfile(profileId, true, true)),
  dispatchResetBrandProfile: () => dispatch(resetBrandProfile())
});

export default connect(mapStateToProps, mapDispatchToProps)(BrandProfile);
